import { scaleLinear } from 'd3-scale';
import { animated, easings, useSpring } from '@react-spring/web';
import {
  LINE_HEIGHT,
  TICK_HEIGHT,
  TICK_PADDING,
  FONT_SIZE,
  CORRECT_ANSWER_ANIMATION_DURATION,
} from './constants';
import { getDisplayValues } from './utils';

const TRIANGLE_SIZE = 24;

export const AxisBottom = ({
  rangeMin,
  rangeMax,
  chartWidth,
  numberOfTicksTarget,
  textColor,
  lineColor,
  tickColor,
  scale,
  correctAnswer,
  shouldShowCorrectAnswer,
  tickWidth,
}: {
  rangeMin: number;
  rangeMax: number;
  chartWidth: number;
  numberOfTicksTarget: number;
  textColor: string;
  lineColor: string;
  tickColor: string;
  scale: number;
  correctAnswer: number;
  shouldShowCorrectAnswer: boolean;
  tickWidth: number;
}) => {
  const domain = getDisplayValues(rangeMin, rangeMax, scale);

  const xScale = scaleLinear()
    .domain([rangeMin, rangeMax])
    .range([0, chartWidth]);

  const isInDomain = (value: number) => domain.includes(value);
  const ticks = xScale
    .ticks(numberOfTicksTarget)
    .filter(isInDomain)
    .map((value) => ({
      value,
      xOffset: xScale(value),
    }));

  const isRangeMinInTicks = ticks.some(({ value }) => value === rangeMin);
  const isRangeMaxInTicks = ticks.some(({ value }) => value === rangeMax);

  // We always want to show the min and max ticks
  if (!isRangeMinInTicks) {
    ticks.push({ value: rangeMin, xOffset: xScale(rangeMin) });
  }
  if (!isRangeMaxInTicks) {
    ticks.push({ value: rangeMax, xOffset: xScale(rangeMax) });
  }

  const styles = useSpring({
    transform: shouldShowCorrectAnswer ? 'scaleX(1)' : 'scaleX(0)',
    arrowOpacity: shouldShowCorrectAnswer ? 1 : 0,
    lineOpacity: shouldShowCorrectAnswer ? 0.1 : 1,
    lineColor: shouldShowCorrectAnswer ? textColor : lineColor,
    config: {
      easing: easings.easeOutExpo,
      duration: CORRECT_ANSWER_ANIMATION_DURATION,
    },
  });

  const isCorrectAnswerOnMin = correctAnswer === rangeMin;
  const isCorrectAnswerOnMax = correctAnswer === rangeMax;
  const adjustedTickX = isCorrectAnswerOnMin ? 0 : -tickWidth / 2;
  const adjustedTickWidth =
    isCorrectAnswerOnMin || isCorrectAnswerOnMax ? tickWidth / 2 : tickWidth;

  return (
    <>
      {/* Main horizontal line */}
      <animated.rect
        x={0}
        y={0}
        width={chartWidth}
        height={LINE_HEIGHT}
        rx={4}
        ry={4}
        style={{
          fill: styles.lineColor,
          opacity: styles.lineOpacity,
        }}
      />

      {/* Ticks and labels */}
      {ticks.map(({ value, xOffset }) => (
        <g key={value} transform={`translate(${xOffset}, 0)`}>
          <line
            x1={0}
            x2={0}
            y1={LINE_HEIGHT + TICK_PADDING}
            y2={LINE_HEIGHT + TICK_PADDING + TICK_HEIGHT}
            stroke={tickColor}
            opacity={0.5}
          />
          <text
            key={value}
            stroke="none"
            fill={textColor}
            style={{
              fontSize: FONT_SIZE,
              textAnchor: 'middle',
              fontWeight: '400',
              dominantBaseline: 'hanging',
              transform: `translateY(${LINE_HEIGHT + TICK_PADDING + TICK_HEIGHT + TICK_PADDING}px)`,
            }}
          >
            {value}
          </text>
        </g>
      ))}

      {shouldShowCorrectAnswer && (
        <g transform={`translate(${xScale(correctAnswer)}, 0)`}>
          <animated.rect
            y={0}
            x={adjustedTickX}
            width={adjustedTickWidth}
            height={LINE_HEIGHT}
            fill={lineColor}
            rx={4}
            ry={4}
            style={{
              transform: styles.transform,
            }}
          />

          {/* triangle pointing up */}
          <animated.polygon
            points={`-${TRIANGLE_SIZE},0 0,-${TRIANGLE_SIZE} ${TRIANGLE_SIZE},0`}
            fill={lineColor}
            opacity={styles.arrowOpacity}
            transform={`translate(0, ${LINE_HEIGHT + TICK_PADDING + TRIANGLE_SIZE / 2})`}
          />
        </g>
      )}
    </>
  );
};
